import { useEffect, useRef } from 'react'
import Styles from './InlineComboBox.module.scss'
import { useFilter } from '@react-aria/i18n'
import { useComboBoxState } from '@react-stately/combobox'
import { useObjectRef } from '@react-aria/utils'
import { useComboBox } from '@react-aria/combobox'
import { ListBoxBase } from 'components/ListBox/ListBoxBase'
import { ComboBoxProps } from './ComboBox'

/**
 * This combobox renders the list as a sibling of the input, and does not use
 * any floating popover. It can be used, for example, to render an embedded
 * combobox inside a dropdown trigger.
 */
export function InlineComboBox(props: ComboBoxProps) {
  const { contains } = useFilter({ sensitivity: 'base' })
  const inputRef = useObjectRef<HTMLInputElement>(null)
  const listBoxRef = useRef<HTMLDivElement>(null)
  const popoverRef = useObjectRef<HTMLDivElement>(null)
  const state = useComboBoxState({
    ...props,
    menuTrigger: 'focus',
    allowsEmptyCollection: false,
    defaultFilter: contains,
  })
  const { inputProps, listBoxProps } = useComboBox({ ...props, inputRef, listBoxRef, popoverRef }, state)

  /**
   * This is a hack to open the combobox list on mount. For some reason the
   * menuTrigger: 'focus' is not working on first focus. I suspect this has to
   * due with the fact that the combobox is embedded in a popover, but we need
   * to investigate further.
   */
  useEffect(() => {
    const timeout = setTimeout(() => {
      state.open()
      inputRef.current?.focus()
    }, 0)
    return () => clearTimeout(timeout)
  }, [inputRef, state])

  return (
    <div className={Styles.wrapper}>
      <div>
        <input autoFocus {...inputProps} ref={inputRef} className={Styles.input} />
        {state.isOpen && <div className={Styles.divider} />}
      </div>
      {state.isOpen && (
        <div className={Styles.list} ref={popoverRef}>
          <ListBoxBase {...listBoxProps} ref={listBoxRef} state={state} isVirtualized={false} />
        </div>
      )}
    </div>
  )
}
